import { apiRequest, createUrlWithCenterId } from '../utils/apiHelpers';
import { sortByNombre } from '../utils/sortByNombre';

export interface EstadoTarea {
  id: number;
  nombre: string;
  descripcion: string;
}

export interface TipoTarea {
  id: number;
  nombre: string;
}

export interface TipoSolicitud {
  id: number;
  nombre: string;
}

export interface Prioridad {
  id: number;
  nombre: string;
}

export interface Periodicidad {
  id: number;
  nombre: string;
}

export interface TipoProducto {
  id: number;
  nombre: string;
  descripcion?: string;
}

export interface Contacto {
  id: number;
  tipoDeContacto: number;
  fecha: string;
  detalles?: string;
  estadoVisitaTecnica?: number;
  sucursalId: number;
  sucursal?: Sucursal;
  usuarioId: number;
  usuario?: User;
}

export interface Sucursal {
  id: number;
  nombre: string;
  clienteId: number;
  cliente?: {
    id: number;
    nombre: string;
  };
}

export interface Presupuesto {
  id: number;
  numero: string;
  descripcion: string;
  fecha: string;
  estado: number;
  sucursalId: number;
  sucursal?: Sucursal;
}

export interface User {
  id: number;
  nombre?: string;
  apellido?: string;
  alias: string;
  mail: string;
}

export interface Tarea {
  id: number;
  nombre: string;
  descripcion?: string;
  sucursalId: number;
  sucursal?: Sucursal;
  clienteId?: number;
  presupuestoId?: number;
  presupuesto?: Presupuesto;
  contactoId?: number;
  contacto?: Contacto;
  tipoDeTareaId: number;
  tipoDeTarea?: TipoTarea;
  tipoSolicitudId?: number;
  tipoSolicitud?: TipoSolicitud;
  periodicidadId?: number;
  periodicidad?: Periodicidad;
  prioridadId?: number;
  prioridad?: Prioridad;
  tipoDeProductoId?: number;
  tipoDeProducto?: TipoProducto;
  tipoDeElementoId?: number;
  fecha: string;
  fechaFin?: string;
  fechaRecepcion?: string;
  estadoTareaId: number;
  estadoTarea?: EstadoTarea;
  usuarioId: number;
  usuario?: User;
  duracion: number;
  frecuencia: number;
  archivoId?: number;
  remitoId?: number;
  activo: boolean;
}

export interface TareaCreateRequest {
  nombre: string;
  descripcion?: string;
  sucursalId: number;
  clienteId?: number;
  /** null = sin valor (no enviar 0: viola FKs en PostgreSQL). */
  presupuestoId?: number | null;
  contactoId?: number | null;
  tipoDeTareaId: number;
  tipoSolicitudId?: number | null;
  periodicidadId?: number | null;
  prioridadId?: number | null;
  tipoDeProductoId?: number | null;
  tipoDeElementoId?: number | null;
  fecha: string;
  fechaFin?: string;
  fechaRecepcion?: string;
  estadoTareaId: number;
  usuarioId: number;
  duracion: number;
  frecuencia: number;
  archivoId?: number | null;
  remitoId?: number | null;
  activo: boolean;
}

export interface TareaUpdateRequest extends TareaCreateRequest {
  id: number;
}

export const tareaService = {
  async getAll(): Promise<Tarea[]> {
    const endpoint = createUrlWithCenterId('/api/Tareas');
    return apiRequest(endpoint);
  },

  async getTareasPendientes(): Promise<Tarea[]> {
    const endpoint = createUrlWithCenterId('/api/Tareas/pendientes');
    return apiRequest(endpoint);
  },

  async getSolicitudes(): Promise<Tarea[]> {
    const endpoint = createUrlWithCenterId('/api/Tareas/solicitudes');
    return apiRequest(endpoint);
  },

  async getById(id: number): Promise<Tarea> {
    const endpoint = createUrlWithCenterId(`/api/Tareas/${id}`);
    return apiRequest(endpoint);
  },

  async create(tarea: TareaCreateRequest): Promise<Tarea> {
    const endpoint = createUrlWithCenterId('/api/Tareas');
    return (await apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(tarea),
    })) as Tarea;
  },

  async update(id: number, tarea: TareaUpdateRequest): Promise<Tarea | null> {
    const endpoint = createUrlWithCenterId(`/api/Tareas/${id}`);
    return (await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(tarea),
    })) as Tarea | null;
  },

  async delete(id: number): Promise<void> {
    const endpoint = createUrlWithCenterId(`/api/Tareas/${id}`);
    await apiRequest(endpoint, {
      method: 'DELETE',
    });
  },

  async getByUsuario(usuarioId: number): Promise<Tarea[]> {
    const endpoint = createUrlWithCenterId(`/api/Tareas/usuario/${usuarioId}`);
    return (await apiRequest(endpoint)) as Tarea[];
  },

  async getBySucursal(sucursalId: number): Promise<Tarea[]> {
    const endpoint = createUrlWithCenterId(`/api/Tareas/sucursal/${sucursalId}`);
    return (await apiRequest(endpoint)) as Tarea[];
  },

  // Métodos para obtener datos de dropdowns
  async getTiposTarea(): Promise<TipoTarea[]> {
    return apiRequest('/api/Tareas/dropdowns/tipos-tarea');
  },

  async getTiposProducto(): Promise<TipoProducto[]> {
    return apiRequest('/api/Tareas/dropdowns/tipos-producto');
  },

  async getPeriodicidades(): Promise<Periodicidad[]> {
    return apiRequest('/api/Tareas/dropdowns/periodicidades');
  },

  async getClientes(): Promise<any[]> {
    const rows = await apiRequest('/api/Tareas/dropdowns/clientes');
    return sortByNombre(rows || []);
  },

  async getSucursales(): Promise<Sucursal[]> {
    const rows = await apiRequest('/api/Tareas/dropdowns/sucursales');
    return sortByNombre(rows || []);
  },
  async getSucursalesByCliente(clienteId: number): Promise<Sucursal[]> {
    const rows = await apiRequest(`/api/Sucursales/cliente/${clienteId}`);
    return sortByNombre(rows || []);
  },
  async getEstadosDeTarea(): Promise<EstadoTarea[]> {
    return apiRequest('/api/EstadosDeTarea');
  },
  async getPrioridades(): Promise<Prioridad[]> {
    return apiRequest('/api/Prioridades');
  },
  async getTiposSolicitud(): Promise<TipoSolicitud[]> {
    return apiRequest('/api/TiposDeSolicitud');
  },
  async getUsers(): Promise<User[]> {
    return apiRequest('/api/Users');
  },
};

const defaultEstadoTareaColor = 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';

/** Colores alineados al seed `EstadoTareas` (Ids 1 Pendiente, 2 Asignada, 3 Finalizada). */
export const getEstadoColor = (estado: string | number | undefined | null): string => {
  if (estado === undefined || estado === null) {
    return defaultEstadoTareaColor;
  }
  if (typeof estado === 'number') {
    switch (estado) {
      case 1:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 2:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 3:
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return defaultEstadoTareaColor;
    }
  }
  switch (estado.toLowerCase()) {
    case 'pendiente':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'asignada':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'finalizada':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    default:
      return defaultEstadoTareaColor;
  }
};

export const getPrioridadColor = (prioridad: string): string => {
  switch (prioridad.toLowerCase()) {
    case 'baja':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'media':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'alta':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    case 'crítica':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
};

// Método para obtener presupuestos
export const getPresupuestos = async (): Promise<Presupuesto[]> => {
  return apiRequest('/api/presupuestos');
};
